import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import telegramService from "../utils/telegram";

export type Tab = "home" | "plan" | "stats" | "profile";

export type ScreenId =
  | "premium"
  | "meal_plan"
  | "recipes"
  | "chat_coach"
  | "edit_meal"
  | "friends"
  | "lenta"
  | "report_detail";

export interface ScreenEntry {
  id: ScreenId;
  params?: Record<string, string>;
}

interface NavigationValue {
  tab: Tab;
  /** Push qilingan sub-screen'lar steki; oxirgisi ko'rinadi */
  stack: ScreenEntry[];
  /** Stack tepasidagi screen (bo'sh bo'lsa null — tab ko'rinadi) */
  screen: ScreenEntry | null;
  setTab: (tab: Tab) => void;
  push: (id: ScreenId, params?: Record<string, string>) => void;
  pop: () => void;
  replace: (id: ScreenId, params?: Record<string, string>) => void;
}

const NavigationContext = createContext<NavigationValue | null>(null);

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tab, setTabState] = useState<Tab>("home");
  const [stack, setStack] = useState<ScreenEntry[]>([]);

  const setTab = useCallback((next: Tab) => {
    setStack([]);
    setTabState(next);
  }, []);

  const push = useCallback((id: ScreenId, params?: Record<string, string>) => {
    setStack((prev) => [...prev, { id, params }]);
  }, []);

  const pop = useCallback(() => {
    setStack((prev) => prev.slice(0, -1));
  }, []);

  const replace = useCallback((id: ScreenId, params?: Record<string, string>) => {
    setStack((prev) => [...prev.slice(0, -1), { id, params }]);
  }, []);

  // Telegram BackButton: stack bo'sh bo'lmasa ko'rinadi va pop qiladi;
  // bo'sh stackda yashirin — Telegram o'zi appni yopadi
  useEffect(() => {
    if (stack.length === 0) return;
    return telegramService.onBack(pop);
  }, [stack.length, pop]);

  const value = useMemo<NavigationValue>(
    () => ({
      tab,
      stack,
      screen: stack.length > 0 ? stack[stack.length - 1] : null,
      setTab,
      push,
      pop,
      replace,
    }),
    [tab, stack, setTab, push, pop, replace],
  );

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
};

export function useNavigation(): NavigationValue {
  const value = useContext(NavigationContext);
  if (!value) {
    throw new Error("useNavigation must be used within NavigationProvider");
  }
  return value;
}
